'use client' // Error components must be Client Components

import { Button } from '@/components/primitives/button'
import { NextIntlClientProvider, useTranslations } from 'next-intl'
import enMessages from '@/messages/en.json'
import frMessages from '@/messages/fr.json'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect } from 'react'

// MARK: Locale for root error boundary
/**
 * Derives `fr` | `en` from the URL because this root `error.tsx` renders **without** the
 * `[locale]/layout` tree (the segment that threw is replaced), so `NextIntlClientProvider`
 * is not an ancestor unless we wrap the UI here.
 *
 * @param pathname - Example: `"/fr/my-admin/events"` or `"/en/connexion"`
 */
function locale_from_pathname(pathname: string | null): 'fr' | 'en' {
    const segments = pathname?.split('/').filter(Boolean) ?? []
    const first = segments[0]
    return first === 'en' ? 'en' : 'fr'
}

// MARK: Database unreachable detection
/**
 * Narrowing helper: root `error.tsx` receives thrown values; some environments type `Error` loosely.
 */
function error_like_message(error: unknown): string | null {
    if (typeof error !== 'object' || error === null) {
        return null
    }
    if (!('message' in error)) {
        return null
    }
    const msg = (error as { message: unknown }).message
    return typeof msg === 'string' ? msg : null
}

/**
 * Detects Prisma / connection failures that surface when PostgreSQL is unreachable.
 * Matches normalized messages from `@/lib/server-utils/prisma/prisma-error` and common Prisma codes.
 */
function is_database_unreachable_error(error: unknown): boolean {
    const message = error_like_message(error) ?? ''
    if (message.includes("Can't reach database")) {
        return true
    }
    if (
        /\bP1001\b|\bP1017\b|PrismaClientInitializationError|Server has closed the connection/i.test(
            message,
        )
    ) {
        return true
    }
    if (typeof error !== 'object' || error === null || !('cause' in error)) {
        return false
    }
    return is_database_unreachable_error((error as { cause: unknown }).cause)
}

function ErrorContent({
    error,
    reset,
}: {
    error: Error & { digest?: string }
    reset: () => void
}) {
    const router = useRouter()
    const pathName = usePathname()
    const locale = locale_from_pathname(pathName)
    const t = useTranslations('ui.auth')
    const t_boundary = useTranslations('ui.error_boundary')

    const is_db_down = is_database_unreachable_error(error)

    let heading: string
    if (is_db_down) {
        heading = t('connection_health_unavailable')
    } else if (error.message?.startsWith('Erreur:')) {
        heading = error.message
    } else {
        heading = t_boundary('generic_title')
    }

    useEffect(() => {
        console.error(error)
    }, [error])

    return (
        <div className="w-full h-full flex-1 flex-col flex justify-center items-center">
            <h2 className="text-lg">{heading}</h2>
            <div className="flex flex-row gap-4">
                <Button onClick={() => router.back()}>{t_boundary('back')}</Button>
                <Button onClick={() => reset()}>{t_boundary('retry')}</Button>
                <Button
                    onClick={() => {
                        router.replace(pathName ?? '/')
                        router.refresh()
                    }}
                >
                    {t_boundary('clear_filters')}
                </Button>
                <Button onClick={() => router.replace(`/${locale}/connexion`)}>{t_boundary('login')}</Button>
            </div>
        </div>
    )
}

export default function Error({
    error,
    reset,
}: {
    error: Error & { digest?: string }
    reset: () => void
}) {
    const pathName = usePathname()
    const locale = locale_from_pathname(pathName)
    const messages = locale === 'en' ? enMessages : frMessages

    return (
        <NextIntlClientProvider locale={locale} messages={messages}>
            <ErrorContent error={error} reset={reset} />
        </NextIntlClientProvider>
    )
}
